import { buildAuthorizeUrl as buildCoreAuthorizeUrl } from '@core/login-url';

/** Bootstrap values served by GET /api/config. */
export interface RuntimeConfig {
  userPoolId: string;
  clientId: string;
  /** Byte-exact from the registry — never rebuild this from window.location. */
  redirectUri: string;
  domainName: string;
  idpName: string;
  scopes: string[];
  entraTenantId: string;
  region: string;
}

export interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function tokenEndpoint(config: RuntimeConfig): string {
  return `https://${config.domainName}/oauth2/token`;
}

/** The console's own sign-in redirect: the shared authorize URL plus the PKCE parameters. */
export function buildAuthorizeUrl(
  config: RuntimeConfig,
  params: { state: string; challenge: string },
): string {
  return buildCoreAuthorizeUrl({
    domainName: config.domainName,
    idpName: config.idpName,
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    scopes: config.scopes,
    extraParams: {
      code_challenge: params.challenge,
      code_challenge_method: 'S256',
      state: params.state,
    },
  });
}

async function postToken(config: RuntimeConfig, body: URLSearchParams): Promise<TokenSet> {
  const response = await fetch(tokenEndpoint(config), {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Token endpoint returned ${response.status}: ${text}`);
  }

  const json = (await response.json()) as TokenResponse;
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + json.expires_in * 1000,
  };
}

export function exchangeCode(
  config: RuntimeConfig,
  params: { code: string; verifier: string },
): Promise<TokenSet> {
  return postToken(
    config,
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code: params.code,
      // Must match the authorize request byte for byte, including any trailing slash.
      redirect_uri: config.redirectUri,
      code_verifier: params.verifier,
    }),
  );
}

/**
 * Cognito does not return a new refresh token on this call, so callers keep the original.
 *
 * A refresh re-runs the pre-token-generation trigger, which is a feature rather than an
 * implementation detail: if an admin is removed from the Entra group, or the console's app client is
 * disabled, the next refresh fails and the session ends within a couple of minutes.
 */
export async function refreshTokens(
  config: RuntimeConfig,
  refreshToken: string,
): Promise<TokenSet> {
  const tokens = await postToken(
    config,
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  );
  return { ...tokens, refreshToken };
}

/** Best-effort. EnableTokenRevocation is set on every broker app client. */
export async function revokeToken(config: RuntimeConfig, refreshToken: string): Promise<void> {
  await fetch(`https://${config.domainName}/oauth2/revoke`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ token: refreshToken, client_id: config.clientId }),
  }).catch(() => {
    // Sign-out must not fail because revocation did. The in-memory tokens are dropped regardless.
  });
}

